import { useRef, useEffect, useCallback } from 'react';
import { useEventContext } from '@providers/EventProvider';

import { useAvaturnHelper } from '@features/avatar/helpers';
import AvaturnService from '@features/avaturn/services/AvaturnService';
import { ChangeAvatarTypes, IXRHrBrandTypes } from '@clients/ek/interfaces';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { AvaturnStatuses } from '@features/avaturn/interfaces';
import ParticipantsService from '@services/ParticipantsService';
import { AvatarTypes } from '@interfaces/avatar';
import { MytaverseLogger } from '@helpers/logger';
import { getCatchErrorMessage } from '@helpers/error';
import { useChangeAvatarState } from '../context';
import AvaturnLogger from '@features/avaturn/pages/Avaturn/provider/logger';

const useTryOnInit = () => {
  const { isEventRun } = useEventContext();
  const isInitPreloadSend = useRef(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const { getChangeAvatarParams } = useAvaturnHelper();
  const { currentEvent } = useMytaverseEvent();

  const { user } = useMytaverse();

  const hasUniformed = user?.avatars.find(({ tags = [] }) =>
    tags.includes('UNIFORM'),
  );

  const { setAvaturnId } = useChangeAvatarState();

  const changeAvatar = useCallback(
    async (param: {
      avatarId: string;
      brand: string;
      ixrCredentialStatus: string;
      gender: string;
      persona?: string;
      tags: string[];
    }) => {
      AvaturnLogger.log(`changeAvatar init ${JSON.stringify(param)}`);
      const avatar = await AvaturnService.changeAvatar(param);
      setAvaturnId(avatar.avaturnId);
      fetchAvatarInfo(avatar.avaturnId);
    },
    [],
  );

  const fetchAvatarInfo = useCallback(async (avaturnId: string) => {
    timeoutIdRef.current = setTimeout(async () => {
      try {
        const data = await AvaturnService.getAvatarInfo(avaturnId);

        if (
          data?.status &&
          [
            AvaturnStatuses.FAILED_CREATION,
            AvaturnStatuses.FAILED_EXPORTING,
            AvaturnStatuses.FAILED,
          ].includes(data.status)
        ) {
          timeoutIdRef.current = null;
        } else if (data.avatarUrl && data?.status === AvaturnStatuses.LIMITED) {
          await ParticipantsService.addParticipantAvatarToList({
            avatarType: AvatarTypes.AVATURN,
            avatarId: data.avatarId,
          });
          timeoutIdRef.current = null;
        } else {
          fetchAvatarInfo(avaturnId);
        }
      } catch (e) {
        timeoutIdRef.current = null;
        MytaverseLogger.log(`fetchAvatarInfo: ${getCatchErrorMessage(e)}`);
      }
    }, 1000);
  }, []);

  useEffect(() => {
    if (
      !hasUniformed &&
      isEventRun &&
      !isInitPreloadSend.current &&
      currentEvent?.eventTags?.includes('FullTimeSDHub') &&
      user.activeAvatarId
    ) {
      const param = getChangeAvatarParams(ChangeAvatarTypes.UniformTryOn);
      if (param && param.brand === IXRHrBrandTypes.SD) {
        changeAvatar({ ...param, avatarId: user.activeAvatarId });

        isInitPreloadSend.current = true;
      }
    }
  }, [isEventRun, hasUniformed, user?.activeAvatarId]);

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, []);
};

export default useTryOnInit;
